import React from 'react'
import { useTranslation } from 'react-i18next'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatInteger,
    formatNumber,
    formatPercentage,
} from '../../packages/utils/numberFormatters'
import { FactsTable, FactsTableRowFlexibleColumns } from '../components/FactsTable'
import { WarrantEntity, WarrantSubscriptionPeriod } from '../../types/WarrantEntity'

const getWarrantSubscriptionPeriod = (subscriptionPeriod: WarrantSubscriptionPeriod) => {
    const subStartDate = subscriptionPeriod.subStartDate
    const subEndDate = subscriptionPeriod.subEndDate

    return <span>{formatDateYYYYMMDD(subStartDate) + ' - ' + formatDateYYYYMMDD(subEndDate)}</span>
}

function getAmount(subscriptionPeriod: WarrantSubscriptionPeriod) {
    return (
        ((subscriptionPeriod.sharesSubscribed || 0) + (subscriptionPeriod.guarantorAllocation || 0)) *
        (subscriptionPeriod.warrantSubPrice || 0)
    )
}

export const getSubscriptionRate = (warrant: WarrantEntity, subscriptionPeriod: WarrantSubscriptionPeriod) => {
    if (subscriptionPeriod.sharesSubscribed === null) return null
    return (
        (100 * subscriptionPeriod.sharesSubscribed * (subscriptionPeriod.warrantsRequired || 0)) /
        (subscriptionPeriod.sharesReceived || 0) /
        (warrant.totalNumberOfWarrants || 0)
    )
}

export const getGuarantorRate = (warrant: WarrantEntity, subscriptionPeriod: WarrantSubscriptionPeriod) => {
    if (subscriptionPeriod.sharesSubscribed === null) return null
    return (
        (100 * subscriptionPeriod.guarantorAllocation * (subscriptionPeriod.warrantsRequired || 0)) /
        (subscriptionPeriod.sharesReceived || 0) /
        (warrant.totalNumberOfWarrants || 0)
    )
}

export const warrantOutcomeHasContent = (warrant: WarrantEntity) => {
    return getPreviousSubscriptionPeriods(warrant).length > 0
}

export const getPreviousSubscriptionPeriods = (warrant: WarrantEntity) => {
    const today = new Date().toISOString()
    return warrant.subscriptionPeriods?.filter((sp) => sp.subEndDate && sp.subEndDate < today)
}

export const calculateTotalAmount = (subscriptionPeriods: WarrantSubscriptionPeriod[]) => {
    return subscriptionPeriods.reduce((acc, sp) => acc + getAmount(sp), 0)
}

export const calculateTotalAmountAndFormat = (locale: string, warrant: WarrantEntity) => {
    const { subscriptionPeriods, currency } = warrant

    const totalAmount = calculateTotalAmount(subscriptionPeriods)

    if (totalAmount)
        return formatBigMoney(totalAmount, {
            locale,
            decimals: 0,
            currencySymbol: currency,
        })
    else return '-'
}

const sumPropertyAndFormatNumber = (
    subscriptionPeriods: WarrantSubscriptionPeriod[],
    prop: keyof WarrantSubscriptionPeriod,
    locale: string,
) => {
    const sum = subscriptionPeriods.reduce((acc: number | null, sp) => {
        if (sp[prop] === null) return acc

        return (acc ?? 0) + (sp[prop] as number)
    }, null)

    if (sum === null) return null

    return formatNumber(sum, { decimals: 0, locale })
}

function WarrantOutcome({ warrant }: { warrant: WarrantEntity }) {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const tableRows = []
    const subscriptionPeriods = getPreviousSubscriptionPeriods(warrant)

    tableRows.push(
        <FactsTableRowFlexibleColumns
            key={tableRows.length}
            firstColumnValue={'Teckningsperiod'}
            columns={subscriptionPeriods}
            getColumnValue={(subscriptionPeriod) => getWarrantSubscriptionPeriod(subscriptionPeriod)}
            lastColumnValue={() => subscriptionPeriods.length > 1 && 'Totalt'}
        />,
    )

    tableRows.push(
        <FactsTableRowFlexibleColumns
            key={tableRows.length}
            firstColumnValue={'Teckningsgrad'}
            columns={subscriptionPeriods}
            getColumnValue={(subscriptionPeriod) =>
                subscriptionPeriod.sharesSubscribed === null
                    ? '-'
                    : formatPercentage(getSubscriptionRate(warrant, subscriptionPeriod), { locale, decimals: 1 })
            }
            lastColumnValue={() =>
                subscriptionPeriods.length > 1 &&
                formatPercentage(
                    subscriptionPeriods.reduce((acc, sp) => acc + (getSubscriptionRate(warrant, sp) || 0), 0),
                    { locale },
                )
            }
        />,
    )

    tableRows.push(
        <FactsTableRowFlexibleColumns
            key={tableRows.length}
            firstColumnValue={'Antal tecknade aktier'}
            columns={subscriptionPeriods}
            getColumnValue={(subscriptionPeriod) => {
                return subscriptionPeriod.sharesSubscribed === null
                    ? 'Inte kommunicerat ännu'
                    : formatInteger(subscriptionPeriod.sharesSubscribed, { locale })
            }}
            lastColumnValue={() =>
                subscriptionPeriods.length > 1 &&
                formatInteger(
                    subscriptionPeriods.reduce((acc, sp) => acc + (sp.sharesSubscribed || 0), 0),
                    { locale },
                )
            }
        />,
    )

    const guarantorAllocationSum = sumPropertyAndFormatNumber(subscriptionPeriods, 'guarantorAllocation', locale)

    if (guarantorAllocationSum !== null) {
        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={'Riktad emission till garanter'}
                columns={subscriptionPeriods}
                getColumnValue={(subscriptionPeriod) =>
                    subscriptionPeriod.guarantorAllocation === null
                        ? '-'
                        : formatNumber(subscriptionPeriod.guarantorAllocation, {
                              locale,
                              decimals: 0,
                          })
                }
                lastColumnValue={() => subscriptionPeriods.length > 1 && guarantorAllocationSum}
            />,
        )
    }

    tableRows.push(
        <FactsTableRowFlexibleColumns
            key={tableRows.length}
            firstColumnValue={'Belopp'}
            columns={subscriptionPeriods}
            getColumnValue={(subscriptionPeriod) =>
                subscriptionPeriod.sharesSubscribed === null
                    ? '-'
                    : formatBigMoney(getAmount(subscriptionPeriod), {
                          locale,
                          decimals: 0,
                          currencySymbol: warrant.currency,
                          tooltip: true,
                      })
            }
            lastColumnValue={() => subscriptionPeriods.length > 1 && calculateTotalAmountAndFormat(locale, warrant)}
        />,
    )

    const guarantorRemunerationSharesSum = sumPropertyAndFormatNumber(
        subscriptionPeriods,
        'guarantorRemunerationShares',
        locale,
    )

    if (guarantorRemunerationSharesSum !== null) {
        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={'Ersättning i aktier till garanter '}
                columns={subscriptionPeriods}
                getColumnValue={(subscriptionPeriod) =>
                    subscriptionPeriod.guarantorRemunerationShares === null
                        ? '-'
                        : formatNumber(subscriptionPeriod.guarantorRemunerationShares, {
                              locale,
                              decimals: 0,
                          })
                }
                lastColumnValue={() => subscriptionPeriods.length > 1 && guarantorRemunerationSharesSum}
            />,
        )
    }

    return <FactsTable rows={tableRows} />
}

export default WarrantOutcome
